import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTable } from 'react-table';
import { RootState } from '../../store/store';
import { URLs } from '../../Constants/Consts';
import { fetchApi } from '../../Helpers/Functions';
import { addChangeLogs } from '../../store/ChangeLogs';
import FilterableTable from '../../components/TableFilter/FilterableTable';
import ChangeLogModal from '../../components/Modal/ChangeLogModal';
import { ChangeLog, ChangeLogType } from '../../types/types';
import { exportToCSV, formatChangelogDate, formatChangelogDateTime } from '../../utils/exportUtils';
import { HiDownload, HiRefresh } from 'react-icons/hi';
import Loader from '../../components/CoreComponents/Loader';

export default function AllChangeLogs() {
  const dispatch = useDispatch();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
  const [selectedChangelogUuid, setSelectedChangelogUuid] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  
  const { SelectedUser } = useSelector((state: RootState) => state.SelectedUser);
  const { ChangeLogsTypes } = useSelector((state: RootState) => state.changelogsTypes);
  const { allDisciplines } = useSelector((state: RootState) => state.Discipline);
  const { ChangeLogs } = useSelector((state: RootState) => state.ChangeLogs);
  const { projects } = useSelector((state: RootState) => state.Project);

  // Fetch changelogs if not already loaded
  useEffect(() => {
    const loadChangelogs = async () => {
      // Only fetch once if:
      // 1. We haven't fetched before
      // 2. We have no changelogs in Redux
      // 3. We have the necessary prerequisites (user project and disciplines)
      // 4. We're online
      if (!hasFetchedRef.current &&
          ChangeLogs.length === 0 && 
          SelectedUser.projectNumber && 
          allDisciplines.length > 0 && 
          navigator.onLine) {
        try {
          hasFetchedRef.current = true;
          setIsInitialLoading(true);
          
          const logs = await Promise.all(
            allDisciplines.map(d => 
              fetchApi(URLs.getChangeLogsPerDisciplineUrl(SelectedUser.projectNumber, d.disciplineId))
                .then(res => res.json())
            )
          );
          
          const flattenedLogs = logs.flat().filter(log => log && !log.status);
          if (flattenedLogs.length > 0) {
            dispatch(addChangeLogs(flattenedLogs));
          }
        } catch (error) {
          console.error('Failed to fetch changelogs:', error);
          hasFetchedRef.current = false; // Allow retry on error
        } finally {
          setIsInitialLoading(false);
        }
      }
    };

    loadChangelogs();
  }, [ChangeLogs.length, SelectedUser.projectNumber, allDisciplines, dispatch]);

  // Helper function to get discipline name
  const getDisciplineName = useCallback((disciplineId: string) => {
    const discipline = allDisciplines.find(d => String(d.disciplineId) === String(disciplineId));
    return discipline?.disciplineName || disciplineId;
  }, [allDisciplines]);

  // Helper function to get project text (number - name)
  const getProjectText = useCallback((projectNumber: string) => {
    const project = projects.find(p => String(p.projectNumber) === String(projectNumber));
    return project ? `${project.projectNumber} - ${project.projectName}` : projectNumber;
  }, [projects]);

  // Define table columns
  const columns = useMemo(() => [
    {
      Header: "Description",
      accessor: "description",
      disableFilter: true,
      width: 400,
    },
    {
      Header: "Type",
      accessor: "changelogTypeId",
      Cell: ({ value }: { value: number }) => {
        const changelogType = ChangeLogsTypes.find((type: ChangeLogType) => type.id === value);
        return changelogType?.typeName || 'Unknown';
      },
      getFilterValue: (value: number) => {
        const changelogType = ChangeLogsTypes.find((type: ChangeLogType) => type.id === value);
        return changelogType?.typeName || 'Unknown';
      },
    },
    {
      Header: "Changed By",
      accessor: "changedBy",
    },
    {
      Header: "Changed On",
      accessor: "changedOn",
      filterType: "date",
      Cell: ({ value }: { value: string }) => formatChangelogDate(value),
    },
    {
      Header: "Project",
      accessor: "projectNumber",
      Cell: ({ value }: { value: string }) => getProjectText(value),
      getFilterValue: (value: string) => getProjectText(value),
    },
    {
      Header: "Discipline",
      accessor: "changedByDisciplineId",
      Cell: ({ value }: { value: string }) => getDisciplineName(value),
      getFilterValue: (value: string) => getDisciplineName(value),
    },
  ], [ChangeLogsTypes, getDisciplineName, getProjectText]);

  // Filter changelogs based on search term
  const filteredChangelogs = useMemo(() => {
    if (!searchTerm.trim()) return ChangeLogs;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return ChangeLogs.filter(log => 
      log.description?.toLowerCase().includes(lowerSearchTerm) ||
      log.changedBy?.toLowerCase().includes(lowerSearchTerm) ||
      log.changedEmail?.toLowerCase().includes(lowerSearchTerm) ||
      log.projectNumber?.toLowerCase().includes(lowerSearchTerm) ||
      log.changedByDisciplineId?.toLowerCase().includes(lowerSearchTerm) ||
      getDisciplineName(log.changedByDisciplineId)?.toLowerCase().includes(lowerSearchTerm) ||
      ChangeLogsTypes.find((type: ChangeLogType) => type.id === log.changelogTypeId)?.typeName?.toLowerCase().includes(lowerSearchTerm)
    );
  }, [ChangeLogs, searchTerm, ChangeLogsTypes, getDisciplineName]);

  // Sort changelogs by date (newest first)
  const sortedChangelogs = useMemo(() => {
    return [...filteredChangelogs].sort((a, b) => 
      new Date(b.changedOn).getTime() - new Date(a.changedOn).getTime()
    );
  }, [filteredChangelogs]);

  // Setup table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data: sortedChangelogs,
  });

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleRowClick = (changelog: ChangeLog) => {
    setSelectedChangelogUuid(changelog.changelogUuid);
  };

  const handleRefresh = async () => {
    if (!navigator.onLine || !SelectedUser.projectNumber || allDisciplines.length === 0) {
      return;
    }

    setIsRefreshing(true);
    try {
      const logs = await Promise.all(
        allDisciplines.map(d => 
          fetchApi(URLs.getChangeLogsPerDisciplineUrl(SelectedUser.projectNumber, d.disciplineId))
            .then(res => res.json())
        )
      );
      
      const flattenedLogs = logs.flat().filter(log => log && !log.status);
      dispatch(addChangeLogs(flattenedLogs));
    } catch (error) {
      console.error('Failed to refresh changelogs:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Calculate filtered count (includes both search and column filters)
  const filteredCount = useMemo(() => {
    if (Object.keys(activeFilters).length === 0) {
      return sortedChangelogs.length;
    }

    return sortedChangelogs.filter(log => {
      return Object.entries(activeFilters).every(([columnId, filterValues]) => {
        if (filterValues.length === 0) return true;
        
        // Get the display value for comparison based on column
        let compareValue = '';
        switch (columnId) {
          case 'changelogTypeId':
            compareValue = ChangeLogsTypes.find((type: ChangeLogType) => type.id === log.changelogTypeId)?.typeName || 'Unknown';
            break;
          case 'changedOn':
            // For date columns, compare just the date part
            compareValue = String(log.changedOn).split('T')[0];
            break;
          case 'projectNumber':
            compareValue = getProjectText(log.projectNumber);
            break;
          case 'changedByDisciplineId':
            compareValue = getDisciplineName(log.changedByDisciplineId);
            break;
          default:
            compareValue = String(log[columnId as keyof ChangeLog] || '');
        }
        
        return filterValues.includes(compareValue);
      });
    }).length;
  }, [sortedChangelogs, activeFilters, ChangeLogsTypes, getProjectText, getDisciplineName]);

  const handleExport = () => {
    // Apply column filters to the data
    let dataToExport = sortedChangelogs;
    
    if (Object.keys(activeFilters).length > 0) {
      dataToExport = sortedChangelogs.filter(log => {
        return Object.entries(activeFilters).every(([columnId, filterValues]) => {
          if (filterValues.length === 0) return true;
          
          // Get the display value for comparison based on column
          let compareValue = '';
          switch (columnId) {
            case 'changelogTypeId':
              compareValue = ChangeLogsTypes.find((type: ChangeLogType) => type.id === log.changelogTypeId)?.typeName || 'Unknown';
              break;
            case 'changedOn':
              // For date columns, compare just the date part
              compareValue = String(log.changedOn).split('T')[0];
              break;
            case 'projectNumber':
              compareValue = getProjectText(log.projectNumber);
              break;
            case 'changedByDisciplineId':
              compareValue = getDisciplineName(log.changedByDisciplineId);
              break;
            default:
              compareValue = String(log[columnId as keyof ChangeLog] || '');
          }
          
          return filterValues.includes(compareValue);
        });
      });
    }

    // Prepare data for export with all fields including hidden ones
    const exportData = dataToExport.map(log => ({
      'Changelog UUID': log.changelogUuid,
      'Type': ChangeLogsTypes.find((type: ChangeLogType) => type.id === log.changelogTypeId)?.typeName || 'Unknown',
      'Description': log.description,
      'Changed By': log.changedBy,
      'Email': log.changedEmail,
      'Changed On': formatChangelogDateTime(log.changedOn),
      'Project': getProjectText(log.projectNumber),
      'Discipline': getDisciplineName(log.changedByDisciplineId),
      'Project Number': log.projectNumber,
      'Punchlist UUID': log.punchlistUuid,
      'CT UUID': log.ctUuid,
    }));

    // Generate filename with timestamp
    const now = new Date();
    const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
    const filename = `Changelogs_${timestamp}`;

    exportToCSV(exportData, filename);
  };

  if (isInitialLoading || isRefreshing) {
    return <Loader loaders={{ isLogsLoading: true }} />;
  }

  return (
    <div className="overflow-x-auto">
      <div className="mb-4 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            All Changelogs
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Total: {filteredCount} changelogs
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || !navigator.onLine}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={!navigator.onLine ? 'You are offline' : 'Refresh changelogs'}
          >
            <HiRefresh size={20} className={isRefreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-1.5 bg-orange-1000 text-white rounded hover:bg-orange-900 transition-colors"
          >
            <HiDownload size={20} />
            Export
          </button>
        </div>
      </div>

      <FilterableTable
        tableProps={{
          getTableProps,
          getTableBodyProps,
          headerGroups,
          rows,
          prepareRow,
        }}
        rowsPerPage={25}
        withSearch={true}
        handleSearch={handleSearch}
        onRowClick={handleRowClick}
        onFilterChange={setActiveFilters}
        initialFilters={activeFilters}
        noMargin={true}
      />

      {/* Changelog Details Modal */}
      {selectedChangelogUuid && (
        <ChangeLogModal
          changelogUuid={selectedChangelogUuid}
          onClose={() => setSelectedChangelogUuid(null)}
        />
      )}
    </div>
  );
}
